import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MIN_SEARCH_CHARS, screenDetailTypes } from '../../../config/constants';
import { EVENT, KEYS } from '../../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getMixpanelGlobalSearchProperties } from '../../../contexts/MixpanelHelper';
import channelsUtil from '../../../helpers/channelsUtil';
import h from '../../../helpers/common';
import url from '../../../helpers/url';
import user from '../../../helpers/user';
import useResizeMenu from '../../../hooks/useResizeMenu';
import { IStore } from '../../../interfaces/StoreTypes';
import { closeSearchInputOverlay } from '../../../store/reducers/headerSlice';
import { LoadProfile } from '../../../store/reducers/profileSlice';
import { clearSearchResult } from '../../../store/reducers/userRevisionSlice';
import { __ } from '../../../translations';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { useHeaderMenuQuery } from '../../../hooks/useHeaderMenuQuery';
import { useProviders } from '../hooks/useProviders';
import { getFilteredGenericMenuItems } from '../utils/headerUtils';
import useLanguage from '../../../hooks/useLanguage';
import type { HeaderMenuItems } from '@intigral/domain/lib/entity/interface/IGetHeaderMenu';
import { canGuestOpenMenuItem, isGuestNavigationAllowed, isGuestUser, isGuestVisibleMenuItem, showGuestPromotionPopup } from '../../../config/guestMode';

function guestMenuScreen(screen?: string): string {
    if (screen === 'selectProfile') {
        return 'select-profile';
    }
    return screen ?? '';
}

export const useHeaderActions = () => {
    // refs
    const searchInputRef: any = useRef(null);

    // state
    const [searchError, setSearchError] = useState('');
    const [searchQuery, setSearchQuery] = useState(url.getQuery('q') == null ? '' : url.getQuery('q'));
    const [clickedProfile, setClickedProfile] = useState(null);
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [passwordVerified, setPasswordVerified] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);

    // redux state
    const searching = useSelector((state: IStore) => state.header.searchOverlay);
    const { selectedProfile, dispatchedActions } = useSelector((state: IStore) => state.profile);
    const userSlice = useSelector((state: IStore) => state?.user);

    // hooks
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const navigation = (route: string) => navigate(route);
    const { data: headerData } = useHeaderMenuQuery();
    const { currentProviderLink } = useProviders();
    const { operators = [], tvodConfig } = useConfigurationSections();
    const { track, incrementProperty, setUserProperties, setSuperProperties }: any = useContext(MixpanelContext);
    const searchPage = url.getDetailsPage(screenDetailTypes.SEARCH);
    const isMobile = useMediaQuery('(max-width: 924px)');
    const location = useLocation();
    const language = useLanguage();
    const currentPath = location.pathname;

    const loadProfileData = useCallback(
        (profileData) => dispatch(LoadProfile({ params: profileData, navigation })),
        [dispatch]
    );

    const guestNavProps = useCallback((props: Record<string, unknown>, menuItem?: HeaderMenuItems) => {
        if (!isGuestUser(user.getUserInfo()) || canGuestOpenMenuItem(menuItem)) {
            return props;
        }

        return {
            ...props,
            onClick: (e: ReactMouseEvent) => {
                showGuestPromotionPopup(e);
            },
        };
    }, []);

    const navItems =
        getFilteredGenericMenuItems(headerData, operators, tvodConfig?.msisdnPrefix)
            ?.map((item) => {
                const isGuest = isGuestUser(user.getUserInfo());
                const guestKidsMenuItem =
                    isGuest &&
                    !selectedProfile?.kidsMode &&
                    item?.screen !== 'kids-home' &&
                    isGuestVisibleMenuItem(item) &&
                    isGuestNavigationAllowed(item);
                const visibleModeOk =
                    item?.visible_mode === 'all' ||
                    (!selectedProfile?.kidsMode && item?.visible_mode === 'parent') ||
                    (selectedProfile?.kidsMode && item?.visible_mode === 'kid') ||
                    guestKidsMenuItem;

                if (visibleModeOk) {
                    if (item?.screen === 'providers') {
                        const prev = item.transformLinkProps;
                        return {
                            ...item,
                            linkComponent: Link,
                            text: `${language === 'en' ? item?.label?.en : item?.label?.ar}`,
                            transformLinkProps: prev
                                ? (args: { href: string }) =>
                                      guestNavProps(prev(args) as Record<string, unknown>, item)
                                : ({ href }: { href: string }) => guestNavProps({ to: href }, item),
                        };
                    }
                    return {
                        ...item,
                        linkComponent: Link,
                        transformLinkProps: ({ href }: { href: string }) => guestNavProps({ to: href }, item),
                        href: `${language === 'en' ? '/en/' : '/'}${guestMenuScreen(item?.screen)}${item?.screenQueryParams || ''}`,
                        text: `${language === 'en' ? item?.label?.en : item?.label?.ar}`,
                        isActive:
                            currentPath ===
                            `${language === 'en' ? '/en/' : '/'}${guestMenuScreen(item?.screen)}${item?.screenQueryParams || ''}`,
                    };
                }
            })
            .filter(Boolean) ?? [];

    const { menuRef } = useResizeMenu(navItems, selectedProfile);

    // handler functions
    const handleSearchFormSubmit = (query: string) => {
        const value = query.trim();
        if (value.length < MIN_SEARCH_CHARS) {
            setSearchError(__('search_input_validation_min_chars', { character: MIN_SEARCH_CHARS }));
        } else {
            incrementProperty(KEYS.INCREMENT.NO_OF_SEARCHES);
            setUserProperties({ [KEYS.PEOPLE.SEARCH_KEYWORDS]: value }, true);
            setUserProperties({ [KEYS.PEOPLE.LAST_SEARCH]: channelsUtil.getCurrentDateTime() });
            track(EVENT.GLOBAL.SEARCH, getMixpanelGlobalSearchProperties(value, h.getScreen()));
            setSearchError('');
            dispatch(clearSearchResult());
            dispatch(closeSearchInputOverlay(false));
            navigate(url.route(searchPage, { q: value }));
        }
    };

    const closePasswordPopupHandler = () => {
        setShowPasswordPopup(false);
        setClickedProfile(null);
    };

    // useEffects
    useEffect(() => {
        if (passwordVerified) {
            setPasswordVerified((s) => {
                setClickedProfile(null);
                setShowPasswordPopup(false);
                return false;
            });
        }
    }, [passwordVerified]);

    useEffect(() => {
        if (dispatchedActions && dispatchedActions.action === 'hasBeenDeleted') {
            setShowConfirmPopup(true);
        }
    }, [dispatchedActions]);

    useEffect(() => {
        const loadProfileFunctions = async () => {
            setLoadingProfile(clickedProfile);
            await loadProfileData(clickedProfile);
            setClickedProfile(null);
        };

        if (clickedProfile) {
            setSuperProperties({
                [KEYS.SUPER.ACTIVE_PROFILE]: clickedProfile.profileName,
            });
            if (clickedProfile.passwordProtected && !passwordVerified) {
                setShowPasswordPopup(true);
            } else {
                loadProfileFunctions();
            }
        }
    }, [clickedProfile]);

    return {
        selectedProfile,
        currentProviderLink,
        isMobile,
        navItems,
        searching,
        searchQuery,
        searchError,
        showPasswordPopup,
        clickedProfile,
        loadingProfile,
        showConfirmPopup,
        searchInputRef,
        dispatch,
        setClickedProfile,
        setSearchQuery,
        setPasswordVerified,
        setLoadingProfile,
        menuRef,
        handleSearchFormSubmit,
        closePasswordPopupHandler,
    };
};
